import React, { useState, useCallback, useEffect, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import { translate } from '../Services/translation';
import StoreLocatorMarker from './StoreLocatorMarker';

export const DEFAULT_MAP_CENTER = {
  lat: 64.469349,
  lng: 11.540807,
};

export const MAP_COMPONENT_RESTRICTIONS = {
  types: ['(regions)'],
  componentRestrictions: {
    country: window?.__litium?.constants?.currentCountry || "NO"
  },
};

const StoreLocator = (props) => {
  const { stores } = props;

  // List stores for map
  const [mapStores, setMapStores] = useState(stores);
  //List stores for sidebar
  const [listStores, setListStores] = useState(stores);

  const [customerLocation, setCustomerLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isDealer, setIsDealer] = useState(false);
  const [isCabin, setIsCabin] = useState(false);

  const [activeMarkerIndex, setActiveMarkerIndex] = useState(-1);

  const [mapReference, setMapReference] = useState(null);
  const [mapsReference, setMapsReference] = useState(null);

  const searchInput = useRef(null);
  const searchBox = useRef(null);

  useEffect(() => {
    let tempStores = mapStores;

    // Nearest filter
    if (customerLocation && mapReference && mapsReference) {
      let nearestStores = [];
      tempStores.forEach((store) => {
        const storeLocation = new mapsReference.LatLng(store.lat, store.lng);
        nearestStores.push({
          index: store.index,
          distance: mapsReference.geometry.spherical.computeDistanceBetween(
            customerLocation,
            storeLocation
          ),
          lat: store.lat,
          lng: store.lng,
        });
      });
      nearestStores.sort((a, b) => {
        return a.distance - b.distance;
      });
      nearestStores = nearestStores.slice(0, 4);

      const nearestStoresIndexes = nearestStores.map((store) => store.index);
      tempStores = tempStores.filter((store) =>
        nearestStoresIndexes.includes(store.index)
      );
      tempStores.sort(
        (a, b) =>
          nearestStoresIndexes.indexOf(a.index) -
          nearestStoresIndexes.indexOf(b.index)
      );

      const bounds = new mapsReference.LatLngBounds();
      bounds.extend(customerLocation);
      for (let i = 0; i < tempStores.length; i++) {
        const position = new mapsReference.LatLng(
          tempStores[i].lat,
          tempStores[i].lng
        );
        bounds.extend(position);
      }
      mapReference.fitBounds(bounds);
    } else if (customerLocation === null && mapReference && mapsReference) {
      const osloLocation = new mapsReference.LatLng(59.911491, 10.757933);
      let nearestStores = [];
      tempStores.forEach((store) => {
        const storeLocation = new mapsReference.LatLng(store.lat, store.lng);
        nearestStores.push({
          index: store.index,
          distance: mapsReference.geometry.spherical.computeDistanceBetween(
            osloLocation,
            storeLocation
          ),
          lat: store.lat,
          lng: store.lng,
        });
      });
      nearestStores.sort((a, b) => {
        return a.distance - b.distance;
      });
      const nearestStoresIndexes = nearestStores.map((store) => store.index);
      tempStores = tempStores.filter((store) =>
        nearestStoresIndexes.includes(store.index)
      );
      tempStores.sort(
        (a, b) =>
          nearestStoresIndexes.indexOf(a.index) -
          nearestStoresIndexes.indexOf(b.index)
      );

      const bounds = new mapsReference.LatLngBounds();
      bounds.extend(osloLocation);
      for (let i = 0; i < tempStores.length; i++) {
        const position = new mapsReference.LatLng(
          tempStores[i].lat,
          tempStores[i].lng
        );
        bounds.extend(position);
      }
      mapReference.fitBounds(bounds);
    }

    setListStores(tempStores);
  }, [mapStores, setListStores, customerLocation, mapReference, mapsReference]);

  useEffect(() => {
    let tempStores = stores;
    if (isDealer != isCabin) {
      tempStores = stores.filter((store) => {
        if (isDealer) {
          return store.isDealer;
        } else if (isCabin) {
          return store.isCabin;
        }
        return false;
      });
    }
    setMapStores(tempStores);
  }, [stores, isDealer, isCabin, setMapStores]);

  let retryCount = 0; // search with exact word (adding comma) first, if no result then retry (with auto completion)
  const onPlaceChange = () => {
    const place = searchBox.current.getPlace();
    if (place.geometry) {
      const location = new mapsReference.LatLng(
        place.geometry.location.lat(),
        place.geometry.location.lng()
      );
      mapReference.panTo(location);
      setCustomerLocation(location);
    } else if (!place.geometry && place.name) {
      const autocompleteService = new mapsReference.places.AutocompleteService();
      const placesService = new mapsReference.places.PlacesService(mapReference);
      autocompleteService.getPlacePredictions(
        {
          input: place.name + (retryCount == 0 ? ',' : ''),
          ...MAP_COMPONENT_RESTRICTIONS,
        },
        (predictions, status) => {
          if (
            status == mapsReference.places.PlacesServiceStatus.OK &&
            predictions &&
            predictions[0] &&
            predictions[0].reference
          ) {
            placesService.getDetails(
              {
                reference: predictions[0].reference,
              },
              (details, status) => {
                if (
                  status == mapsReference.places.PlacesServiceStatus.OK &&
                  details &&
                  details.geometry
                ) {
                  retryCount = 0;
                  const location = new mapsReference.LatLng(
                    details.geometry.location.lat(),
                    details.geometry.location.lng()
                  );
                  mapReference.panTo(location);
                  setCustomerLocation(location);
                  setSearchQuery(predictions[0].description);
                } else {
                  setCustomerLocation(null);
                }
              }
            );
          } else {
            retryCount++;
            if (retryCount > 1) {
              retryCount = 0;
              setCustomerLocation(null);
            } else {
              onPlaceChange();
            }
          }
        }
      );
    } else {
      setCustomerLocation(null);
    }
  };

  const handleOnPlacesChanged = useCallback(
    () => onPlaceChange(),
    [searchBox, mapReference, mapsReference, setCustomerLocation, setSearchQuery]
  );

  useEffect(() => {
    if (!searchBox.current && mapsReference) {
      searchBox.current = new mapsReference.places.Autocomplete(
        searchInput.current,
        {
          ...MAP_COMPONENT_RESTRICTIONS,
        }
      );
      searchBox.current.addListener('place_changed', handleOnPlacesChanged);
    }

    return () => {
      if (mapsReference) {
        searchBox.current = null;
        mapsReference.event.clearInstanceListeners(searchBox);
      }
    };
  }, [searchInput, searchBox, mapsReference, handleOnPlacesChanged]);

  const handleDealerClick = () => setIsDealer(!isDealer);
  const handleCabinClick = () => setIsCabin(!isCabin);
  const searchInputOnChange = (e) => setSearchQuery(e.target.value);

  /* Map */
  const onShowOnMapClick = (e, index) => {
    onMapChildClick(index);
    e.preventDefault();
  };

  const onMarkerClose = () => {
    setActiveMarkerIndex(-1);
  };

  const onDealerSelect = () => {
    const nextButton =
        document.querySelector('.checkout__step.active .checkout__step-content-next button');

    nextButton.click();
  }

  const onMapChildClick = (index) => {
    const store = mapStores.find((store) => store.index == index);
    if (store) {
      mapReference.panTo({
        lat: store.lat,
        lng: store.lng,
      });
      const mapDivHeight = mapReference.getDiv().offsetHeight;
      if (mapDivHeight < 400) {
        mapReference.panBy(0, -mapDivHeight / 3);
      }
    }
    setActiveMarkerIndex(index);
  };

  const onGoogleApiLoaded = ({ map, maps }) => {
    setMapReference(map);
    setMapsReference(maps);

    const bounds = new maps.LatLngBounds();
    for (let i = 0; i < mapStores.length; i++) {
      const position = new maps.LatLng(mapStores[i].lat, mapStores[i].lng);
      bounds.extend(position);
    }
    map.fitBounds(bounds); // Automatically center the map fitting all markers on the screen
  };

  const createMapOptions = (maps) => {
    return {
      streetViewControl: true,
      clickableIcons: false,
      styles: [
        {
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
      ],
    };
  };

  return (
    <div className="store-locator-component">
      <div className="store-locator-search-column">
        <div className="search-action-wrapper">
          <input
            ref={searchInput}
            className="locations-search-input"
            type="text"
            placeholder={translate('general.search')}
            onChange={searchInputOnChange}
            value={searchQuery}
          />

          <div className="search-filters-group">
            <label htmlFor="filterDealer" className="custom-checkbox">
              <input
                id="filterDealer"
                onChange={handleDealerClick}
                checked={isDealer}
                type="checkbox"
              />
              <span className="custom-checkbox-label">
                {translate('storelocator.dealer')}
              </span>
            </label>

            <label htmlFor="filterCabin" className="custom-checkbox">
              <input
                id="filterCabin"
                onChange={handleCabinClick}
                checked={isCabin}
                type="checkbox"
              />
              <span className="custom-checkbox-label">
                {translate('storelocator.cabin')}
              </span>
            </label>
          </div>
        </div>
        <div className="search-result-wrapper">
          <ul className="search-results">
            {listStores.map((store) => (
              <li key={store.index}>
                <div className="store-name">{store.name}</div>
                <div className="store-company">{store.company}</div>
                <div className="store-address">{store.address}</div>
                <div className="store-links">
                  <a
                    className="store-link"
                    href={store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {translate('storelocator.visitlink')}
                  </a>
                  <span className="separator"></span>
                  <a
                    className="store-map-link"
                    onClick={(e) => onShowOnMapClick(e, store.index)}
                  >
                    {translate('storelocator.showonmap')}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="store-locator-map-column">
        <GoogleMapReact
          bootstrapURLKeys={{
            key: window.__litium.constants.googleMapApiKey,
            libraries: ['places', 'geometry'],
          }}
          defaultCenter={DEFAULT_MAP_CENTER}
          defaultZoom={6}
          onChildClick={onMapChildClick}
          onGoogleApiLoaded={onGoogleApiLoaded}
          yesIWantToUseGoogleMapApiInternals={true}
          options={createMapOptions}
        >
          {mapStores.map((store) => (
            <StoreLocatorMarker
              key={store.index}
              lat={store.lat}
              lng={store.lng}
              store={store}
              show={store.index == activeMarkerIndex}
              mapsReference={mapsReference}
              onClose={onMarkerClose}
              onDealerSelect={onDealerSelect}
            />
          ))}
        </GoogleMapReact>
      </div>
    </div>
  );
};

export default StoreLocator;
